import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { MapPin, Calendar, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const HERO_IMG = "https://images.unsplash.com/photo-1465056836041-7f43ac27dcb5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxOZXclMjBaZWFsYW5kJTIwbGFuZHNjYXBlfGVufDB8fHx8MTc3NTk3NDg2OHww&ixlib=rb-4.1.0&q=85";
const FAMILY_PHOTO = "https://customer-assets.emergentagent.com/job_mccartney-homecoming/artifacts/x6rj2ya3_McCartney%20family.JPG";
const IRELAND_IMG = "https://images.unsplash.com/photo-1591789916296-20b02cdbc9e8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwyfHxJcmlzaCUyMGNvdW50cnlzaWRlJTIwZ3JlZW4lMjByb2xsaW5nJTIwaGlsbHN8ZW58MHx8fHwxNzc1OTc0OTY2fDA&ixlib=rb-4.1.0&q=85";
const SHIP_IMG = "https://images.unsplash.com/photo-1775733899893-f9baa5d75c0f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwzfHx2aW50YWdlJTIwc2FpbGluZyUyMHNoaXAlMjAxODAwcyUyMGltbWlncmF0aW9uJTIwdm95YWdlJTIwb2NlYW58ZW58MHx8fHwxNzc1OTc1NzU4fDA&ixlib=rb-4.1.0&q=85";
const BLUFF_COAST_IMG = "https://images.unsplash.com/photo-1772423794919-507b8d965cb8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwzfHxCbHVmZiUyMGhpbGwlMjBOZXclMjBaZWFsYW5kJTIwY29hc3QlMjBvY2VhbnxlbnwwfHx8fDE3NzU5NzU3OTV8MA&ixlib=rb-4.1.0&q=85";
const SOUTHLAND_IMG = "https://images.unsplash.com/photo-1761503405203-4760f9f19ec3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxTb3V0aGxhbmQlMjBOZXclMjBaZWFsYW5kJTIwZmFybWxhbmQlMjBwYXN0b3JhbHxlbnwwfHx8fDE3NzU5NzQ5NjZ8MA&ixlib=rb-4.1.0&q=85";
const REUNION_1977_PHOTO = "https://customer-assets.emergentagent.com/job_mccartney-homecoming/artifacts/n1l392xj_Reunion%201977.JPG";

function useScrollReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      { threshold: 0.1 }
    );
    const elements = ref.current?.querySelectorAll(".animate-on-scroll");
    elements?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
  return ref;
}

export default function HomePage() {
  const pageRef = useScrollReveal();

  return (
    <div ref={pageRef}>
      {/* HERO SECTION */}
      <section data-testid="hero-section" className="relative min-h-[75vh] sm:min-h-[85vh] flex items-center">
        <div className="absolute inset-0 bg-charcoal">
          <img
            src={REUNION_1977_PHOTO}
            alt="McCartney Family Reunion, 1977"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-charcoal/85 via-charcoal/70 to-charcoal/40" />
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-12 sm:py-28 w-full">
          <div className="max-w-2xl">
            <p className="text-[#C9A96E] font-sans text-xs sm:text-base font-semibold uppercase tracking-widest mb-4 sm:mb-6">
              1877 — 2027
            </p>
            <h1
              data-testid="hero-heading"
              className="font-serif text-[1.75rem] leading-snug sm:text-5xl lg:text-6xl font-semibold text-paper sm:leading-tight mb-4 sm:mb-6 drop-shadow-md"
            >
              Help Us Honour 150 Years of McCartney Family History in New Zealand
            </h1>
            <p
              data-testid="hero-intro"
              className="text-[0.95rem] sm:text-xl text-paper/90 font-sans leading-relaxed mb-8 sm:mb-10 max-w-2xl"
            >
              In 2027, descendants and extended family are warmly invited to help
              honour two important milestones — 150 years since Thomas McCartney
              arrived in Bluff, New Zealand from Donegal, Ireland on 7 November
              1877, and 50 years since the last reunion held in 1977.
            </p>

            {/* Event Details */}
            <div className="bg-paper/10 backdrop-blur-sm border border-paper/20 rounded-md p-5 sm:p-8 mb-8 sm:mb-10 max-w-lg">
              <div className="space-y-2 sm:space-y-3">
                <div className="flex items-start gap-3">
                  <Calendar size={16} className="text-bronze mt-0.5 shrink-0 sm:hidden" />
                  <Calendar size={18} className="text-bronze mt-1 shrink-0 hidden sm:block" />
                  <div className="text-paper/90 font-sans text-sm sm:text-lg">
                    <p>Friday 22 January 2027 — Welcome</p>
                    <p>Saturday 23 January 2027 — Function & Dinner</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin size={16} className="text-bronze mt-0.5 shrink-0 sm:hidden" />
                  <MapPin size={18} className="text-bronze mt-1 shrink-0 hidden sm:block" />
                  <p className="text-paper/90 font-sans text-sm sm:text-lg">
                    Croydon Lodge, Gore, Southland
                  </p>
                </div>
              </div>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Link to="/register">
                <Button
                  data-testid="hero-register-button"
                  className="bg-bronze hover:bg-bronze/90 text-paper font-sans text-sm sm:text-base px-6 sm:px-8 py-5 sm:py-6 h-auto rounded-md"
                >
                  Register Your Interest
                  <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
              <a href="#save-the-date">
                <Button
                  data-testid="hero-save-date-button"
                  variant="outline"
                  className="border-paper/40 text-paper hover:bg-forest/80 font-sans text-sm sm:text-base px-6 sm:px-8 py-5 sm:py-6 h-auto rounded-md bg-forest"
                >
                  Save the Date
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* McCartney REUNION 1977 PHOTO + INTRO */}
      <section data-testid="family-photo-section" className="bg-paper py-20 sm:py-24 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="animate-on-scroll lg:col-span-7">
              <div className="overflow-hidden rounded-md border-8 border-paper shadow-lg">
                <img
                  src={FAMILY_PHOTO}
                  alt="The McCartney family"
                  className="w-full h-auto object-cover sepia-photo"
                />
              </div>
              <p className="text-sm text-charcoal/50 font-sans mt-3 italic text-center">
                The McCartney family
              </p>
              <p className="text-xs sm:text-sm text-charcoal/45 font-sans mt-1 text-center leading-relaxed">
                Back row: Maggie Kerse, Jack, Agnes, Francis, Cath Hailes<br />
                Front row: Sylvia Wilkins, Sr Imelda, Thomas (Tommy), Maimie O'Connor, Kevin
              </p>
            </div>
            <div className="animate-on-scroll lg:col-span-5">
              <div className="quote-section">
                <blockquote className="font-serif text-xl sm:text-3xl lg:text-4xl text-charcoal leading-relaxed italic mb-6 sm:mb-8">
                  "We have always had a very strong family tradition and I sincerely hope
                  that other reunions will follow in the future and that you all can keep
                  in touch."
                </blockquote>
                <cite className="not-italic">
                  <span className="block font-sans text-base sm:text-lg text-bronze font-medium">
                    Jack McCartney
                  </span>
                  <span className="block font-sans text-sm text-charcoal/50 mt-1">
                    Address given at the McCartney Family Reunion, 1977
                  </span>
                </cite>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* REMEMBERING THE 1977 REUNION */}
      <section
        data-testid="reunion-1977-section"
        className="bg-paper py-14 sm:py-24 lg:py-32"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="max-w-3xl mx-auto">
            <div className="animate-on-scroll text-center">
              <p className="font-sans text-sm font-medium uppercase tracking-widest text-bronze mb-4">
                50 Years Ago
              </p>
              <h2 className="font-serif text-2xl sm:text-4xl lg:text-5xl font-medium text-charcoal mb-6 sm:mb-8">
                Remembering the 1977 Reunion
              </h2>
              <div className="space-y-4 sm:space-y-6 text-left">
                <p className="text-[0.95rem] sm:text-xl font-sans leading-relaxed text-charcoal/80">
                  The last major McCartney family reunion was held in 1977, marking
                  100 years since Thomas McCartney arrived in New Zealand. It was a
                  gathering that brought descendants and extended family together from
                  across the country and celebrated a century of shared history.
                </p>
                <p className="text-[0.95rem] sm:text-xl font-sans leading-relaxed text-charcoal/80">
                  Jack McCartney's words at that reunion still resonate today — a
                  reminder of the importance of keeping family connections alive and
                  carrying the tradition forward for future generations.
                </p>
                <p className="text-[0.95rem] sm:text-xl font-sans leading-relaxed text-charcoal/80">
                  Now, 50 years on, we have the opportunity to gather again — to
                  remember, to reconnect, and to continue what those before us began.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SAVE THE DATE SECTION */}
      <section
        id="save-the-date"
        data-testid="save-the-date-section"
        className="bg-forest py-14 sm:py-24 lg:py-32"
      >
        <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12 text-center">
          <div className="animate-on-scroll">
            <p className="font-sans text-sm font-medium uppercase tracking-widest text-bronze mb-6">
              Mark Your Calendar
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-paper mb-8">
              Save the Date
            </h2>
            <p className="text-2xl sm:text-3xl font-serif font-semibold text-paper/95 mb-12">
              22–23 January 2027
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register">
                <Button
                  data-testid="save-date-register-button"
                  className="bg-bronze hover:bg-bronze/90 text-paper font-sans text-sm sm:text-base px-6 sm:px-8 py-5 sm:py-6 h-auto rounded-md"
                >
                  Register Your Interest
                  <ArrowRight size={18} className="ml-2" />
                </Button>
              </Link>
              <Link to="/register?helping=true">
                <Button
                  data-testid="save-date-helping-button"
                  variant="outline"
                  className="border-paper/40 text-paper hover:bg-paper/10 font-sans text-sm sm:text-base px-6 sm:px-8 py-5 sm:py-6 h-auto rounded-md bg-transparent"
                >
                  Interested in Helping Out?
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
